"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Plus, ArrowRight, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const faqItems = [{
  question: "Are electric mechanical bulls the same as mechanical bulls?",
  answer: "An electric mechanical bull is a type of powered mechanical bull system. Motors, controllers, operating modes and electrical requirements can vary between models, so buyers should compare the exact machine specifications."
}, {
  question: "How much does a new mechanical bull cost?",
  answer: "New mechanical bull prices vary according to the machine, controller, included equipment, safety arena, blower, accessories and delivery requirements. Compare current inventory and complete package contents rather than relying on one generic price."
}, {
  question: "What should I check when looking at an electric bull for sale?",
  answer: "Review the controller, motion system, documented safety functions, power requirements, operating footprint, included equipment and parts availability for the exact machine."
}, {
  question: "Does a new mechanical bull include an inflatable arena?",
  answer: "It depends on the product package. Some new mechanical bulls may include an inflatable safety arena and blower, while others may be configured differently. Check the individual product listing before purchasing."
}, {
  question: "What electrical supply does a mechanical bull require?",
  answer: "Electrical requirements vary by model. Confirm the requirements for the exact bull, controller and blower before planning installation."
}, {
  question: "Can I buy a mechanical bull for a bar or entertainment venue?",
  answer: "Yes. Buyers purchasing for bars, entertainment venues and other commercial settings should compare the machine's operating footprint, controller, package contents, setup requirements and parts availability against their intended use."
}, {
  question: "Can new mechanical bulls be shipped?",
  answer: "Delivery options depend on the machine, package, destination and receiving requirements. Review the Shipping Information page and provide the product and destination details when making an enquiry."
}, {
  question: "Do you sell replacement parts?",
  answer: "Mechanical Bull Shop lists replacement parts and accessories when available. Confirm compatibility with the exact machine before ordering a controller, inflatable component, blower or other replacement part."
}];
export default function BullFaq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const shouldReduceMotion = useReducedMotion();
  const toggleItem = (index: number) => {
    setOpenIndex(current => current === index ? null : index);
  };
  const transitionConfig = shouldReduceMotion ? {
    duration: 0
  } : {
    duration: 0.22,
    ease: [0.2, 0, 0, 1]
  };
  return <section id="new-mechanical-bull-faq" data-nav="light" className="relative w-full border-t border-border bg-background py-16 text-foreground sm:py-24">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="mb-12 border-b border-border pb-8">
          <div>
            <h2 className="text-3xl font-bold tracking-tight uppercase font-serif sm:text-4xl lg:text-5xl">
              New Mechanical Bull FAQ
            </h2>
          </div>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground font-sans">
            Straight answers to common questions about buying a new mechanical bull, electric systems, equipment packages, power requirements, shipping and replacement parts.
          </p>
        </div>

        {/* Industrial Accordion List */}
        <div className="w-full border-b border-border">
          {faqItems.map((item, index) => {
          const isOpen = openIndex === index;
          return <div key={item.question} data-index={index} className="border-t border-border transition-colors duration-150">
                <Button variant="ghost" onClick={() => toggleItem(index)} className="group flex h-auto w-full items-start justify-between gap-4 px-3 py-5 text-left transition-colors duration-150 hover:bg-muted/50 sm:px-4" aria-expanded={isOpen}>
                  <span className="text-base font-semibold tracking-wide uppercase font-serif text-foreground sm:text-lg">
                    {item.question}
                  </span>

                  <span className="relative mt-0.5 flex size-6 shrink-0 items-center justify-center border border-border bg-card text-foreground group-hover:border-primary group-hover:text-primary">
                    <Plus className={`size-3.5 transition-transform duration-200 ${isOpen ? "rotate-45 text-primary" : "rotate-0"}`} />
                  </span>
                </Button>

                <motion.div initial={false} animate={{
              height: isOpen ? "auto" : 0,
              opacity: isOpen ? 1 : 0
            }} transition={transitionConfig} className="overflow-hidden">
                  <div className="border-l-2 border-primary bg-muted/20 px-4 pt-1 pb-6 sm:px-6">
                    <div className="pt-2 text-sm leading-relaxed text-muted-foreground font-sans sm:text-base">
                      {item.answer}
                    </div>
                  </div>
                </motion.div>
              </div>;
        })}
        </div>

        {/* Bottom Industrial Note & Action Strip */}
        <div className="mt-12 flex flex-col items-start justify-between gap-6 border border-border bg-card p-6 sm:flex-row sm:items-center">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-xs font-semibold tracking-wider uppercase font-mono text-foreground">
              <HelpCircle className="size-4 text-primary" />
              <span>Need help comparing equipment?</span>
            </div>
            <p className="text-sm text-muted-foreground font-sans">
              Send us the model you are considering and your intended use so the available product information can be reviewed in context.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <Button asChild variant="outline" size="sm" className="font-mono text-xs uppercase">
              <Link to="/MechanicalBullPartsAndAccessoriesPage">
                Parts &amp; Accessories
              </Link>
            </Button>
            <Button asChild size="sm" className="font-mono text-xs uppercase">
              <Link to="/RequestQuotePage">
                Request Product Information
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}